import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import ContainedCircleLoader from '@pkg/components/Elements/ContainedCircleLoader';

import { callMetaProxy, type RedirectUrlItems } from '@app/helpers';

import './PagePreview.scss';

interface PagePreviewProps {
    redirectUrl: RedirectUrlItems;
}

interface PageMetaItem {
    content: string;
    property: string;
}

interface PageMetas {
    meta: PageMetaItem[];
}

const PagePreview = ({ redirectUrl }: PagePreviewProps) => {
    const { t } = useTranslation(['power_ups', 'common']);
    const [loadedUrl, setLoadedUrl] = useState('');
    const [showLoader, setShowLoader] = useState(false);
    const [metaImageSrc, setMetaImageSrc] = useState<null | string>(null);
    const [pillCopy, setPillCopy] = useState<null | string>(null);
    const [metaTags, setMetaTags] = useState<PageMetas>();
    const [, setMetaTitle] = useState('');
    const [, setMetaDesc] = useState('');
    const [previousRedirectUrl, setPreviousRedirectUrl] = useState('');

    const debounce = useRef<number>();
    const placeholderImageSrc = '/assets/img/meta-placeholder@2x.jpg';

    useEffect(() => {
        let mounted = true;
        if (mounted) {
            setMetaTags(undefined);
            setMetaTitle('');
            if (!redirectUrl.url) {
                setMetaImageSrc(null);
                setPillCopy('');
            }
            clearTimeout(debounce.current);

            debounce.current = window.setTimeout(function () {
                if (redirectUrl && redirectUrl.url) {
                    // Prevent flashing after state is changed
                    if (previousRedirectUrl === redirectUrl.url) {
                        return;
                    }

                    setPillCopy(null);
                    setShowLoader(true);
                    setMetaImageSrc(null);
                    callMetaProxy({ url: redirectUrl.url }).then((result) => {
                        if (result.ok) {
                            setLoadedUrl(redirectUrl.displayUrl);
                            setShowLoader(false);
                            if (typeof result.value === 'object') {
                                setMetaTags(result.value);
                            }
                        } else {
                            if (result.error && redirectUrl && redirectUrl.url.length > 0) {
                                setShowLoader(false);
                            }
                        }
                    });

                    setPreviousRedirectUrl(redirectUrl.url);
                }
            }, 1000);
        }
        return function cleanup() {
            mounted = false;
        };
    }, [redirectUrl, t, previousRedirectUrl]);

    useEffect(() => {
        if (metaTags) {
            const title = (metaTags?.meta || []).find((tag) => {
                return tag.property === 'og:title';
            });
            title && title.content?.length > 0 && setMetaTitle(title.content);

            const desc = metaTags.meta.find((tag) => {
                return tag.property === 'og:description';
            });
            desc && desc.content?.length > 0 && setMetaDesc(desc.content);

            const image = metaTags.meta.find((tag) => {
                return tag.property === 'og:image';
            });

            if (image && image.content?.length > 0) {
                setMetaImageSrc(image.content);
            } else {
                setMetaImageSrc(placeholderImageSrc);
                setPillCopy(t('no_thumb'));
            }
        }
    }, [metaTags, t]);

    const previewAvailable = useMemo(
        () => (
            <div className="page-preview">
                <div className="page-preview__image-container">
                    <div className="page-preview__image-canvas" style={{ backgroundImage: `url(${metaImageSrc})` }} />
                    {!pillCopy || <div className="page-preview__pill">{pillCopy}</div>}
                </div>

                <div className="page-preview__title">{loadedUrl}</div>
            </div>
        ),
        [metaImageSrc, pillCopy, loadedUrl],
    );

    const loading = useMemo(() => {
        return (
            <div className="page-preview page-preview__loading">
                <div className="page-preview__loader">
                    <ContainedCircleLoader copy={t('hang_tight')} />
                </div>
            </div>
        );
    }, [t]);

    const empty = useMemo(() => {
        return (
            <div className="page-preview page-preview__empty">
                <div className="page-preview page-preview__empty__container">
                    <div className="page-preview__image-container">
                        <div className="page-preview__image-canvas">
                            <img
                                className="page-preview__empty__background"
                                src="/assets/img/empty@2x.png"
                                alt={t('ghost_bg_alt')}
                            />
                        </div>
                    </div>
                    <h3 className="empty-text">{t('looks_empty')}</h3>
                    <div className="line" />
                    <p className="empty-sub-text">{t('add_url_copy')}</p>
                </div>
            </div>
        );
    }, [t]);

    return useMemo(() => {
        if (showLoader) {
            return loading;
        } else if (metaImageSrc) {
            return previewAvailable;
        } else {
            return empty;
        }
    }, [showLoader, metaImageSrc, previewAvailable, empty, loading]);
};

export default PagePreview;
